/**
 * Arc entity → `RenderableEntity` adapter (TYPED).
 *
 * DEFUSE S3 — typed sibling of `../arcToRenderable.ts`. Reads the canonical
 * `ArcEntity` fields directly off the model (`model.ts`: `arcEntitySchema`
 * center / radius / startAngle / endAngle) instead of the legacy
 * `EntityPreview.properties` string bag. Produces a byte-identical
 * `RenderableEntity` (golden-parity) for the same entity.
 *
 * Angle unit: the model stores `startAngle` / `endAngle` in **degrees**
 * (the producer `dxfGeometry.buildArcPreview` writes them into the bag
 * verbatim — no conversion — and the `Arc length` / `Total angle` math is
 * degree-based, `% 360`). The legacy adapter then multiplies by
 * `DEG_TO_RAD` to feed the renderer radians. This typed adapter applies
 * the SAME single `* DEG_TO_RAD` conversion — NOT a double-convert — so
 * the radians match the legacy path exactly.
 *
 * Center z: `arcEntitySchema.center` is the 2D `drawingPointSchema`
 * (no z). The legacy bag hardcodes `"Center Z": "0"`, so legacy reads 0.
 * This adapter feeds `z: 0` to `applyOffset` for identical output.
 *
 * Bbox is computed by sampling the start/end points and any 0/90/180/
 * 270° points that fall inside the arc sweep — identical to legacy.
 *
 * Category B: standalone file; NOT imported into any render path yet.
 */

import type { ArcEntity } from "@dxf-ai-editor/drawing-model";

import type { RenderableEntity, Vec3 } from "../../types";

import {
  buildRenderableShell,
  type AdapterContext,
} from "../adapterContext";
import { buildArcSceneGeometry } from "../sceneGeometryBuilders";

/** Typed adapter for the `Arc` entity (`arcEntitySchema`). */
export function arcFromEntity(
  entity: ArcEntity,
  ctx: AdapterContext,
): RenderableEntity {
  const centerRaw: Vec3 = {
    x: entity.center.x,
    y: entity.center.y,
    // `center` is `drawingPointOptionalZSchema` (S-ARC-CIRCLE-CENTER-Z): the
    // legacy producer emits `Center Z` from this same field and the legacy
    // adapter reads it, so `?? 0` is the parity read (the previous hard-coded
    // 0 predated the schema z and dropped both an imported DXF group-30 z AND
    // the block-flatten z-collapse — @codex #431 round-2 Z sweep).
    z: entity.center.z ?? 0,
  };
  // The shared builder applies the SAME single `* DEG_TO_RAD` conversion the
  // legacy path used (NOT a double-convert) — the model stores startAngle /
  // endAngle in degrees, identical to the bag.
  const { geometry, bounds } = buildArcSceneGeometry(
    centerRaw,
    entity.radius,
    entity.startAngle,
    entity.endAngle,
  );

  return buildRenderableShell(entity, ctx, geometry, bounds);
}
